import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, CircleCheckBig, Shield, Clock, FileCheck, TrendingDown } from "lucide-react";

const IMAGES = {
  mitigation: "https://customer-assets.emergentagent.com/job_calgary-radon/artifacts/6wswmxqh_IMG_7739.jpeg",
  measurement: "https://customer-assets.emergentagent.com/job_calgary-radon/artifacts/5dkeh09l_IMG_7575.jpeg",
};

const PROCESS_STEPS = [
  { num: 1, icon: Shield, title: "Book Test", desc: "Schedule your free radon measurement test" },
  { num: 2, icon: Clock, title: "Testing", desc: "Professional testing using certified equipment" },
  { num: 3, icon: FileCheck, title: "Results", desc: "Detailed report with recommendations" },
  { num: 4, icon: TrendingDown, title: "Mitigation", desc: "Professional installation if needed" },
];

export default function ServicesPage() {
  return (
    <main>
      <div className="min-h-screen">
        <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-blue-50">
          <div className="max-w-7xl mx-auto text-center">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">Professional Services</Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Radon Solutions for<span className="text-blue-600"> Your Home</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From testing to mitigation, we provide comprehensive radon services to keep your family safe.
            </p>
          </div>
        </section>

        <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="services-detail-section">
          <div className="max-w-7xl mx-auto space-y-24">
            {/* Mitigation */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="animate-slide-in-left">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                  <img alt="Radon Mitigation" className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-110" src={IMAGES.mitigation} />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent" />
                </div>
              </div>
              <div className="animate-slide-in-right">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Radon Mitigation</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Professional installation of radon mitigation systems to reduce radon levels in your home to safe levels. Our experienced technicians ensure quality workmanship.
                </p>
                <div className="space-y-3 mb-8">
                  {["Guaranteed safe levels", "Top-notch workmanship", "Quality installations"].map((f) => (
                    <div key={f} className="flex items-start space-x-3">
                      <CircleCheckBig className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{f}</span>
                    </div>
                  ))}
                </div>
                <Link to="/book-test">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-6" data-testid="services-get-started-btn">
                    Get Started <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Measurement */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2 animate-slide-in-left">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                  <img alt="Radon Measurement" className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-110" src={IMAGES.measurement} />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent" />
                </div>
              </div>
              <div className="lg:order-1 animate-slide-in-right">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Radon Measurement</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Accurate radon testing using professional-grade equipment. Get detailed reports and expert recommendations for your property.
                </p>
                <Link to="/book-test">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-6" data-testid="services-measurement-btn">
                    Get Started <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="process-section">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Process</h2>
              <p className="text-xl text-gray-600">Simple, professional, and effective</p>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              {PROCESS_STEPS.map((step) => (
                <Card key={step.num} className="border-2 hover:border-blue-200 transition-all hover:shadow-lg">
                  <CardContent className="p-6 text-center">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl">
                      {step.num}
                    </div>
                    <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <step.icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-blue-600 border-0 overflow-hidden">
              <CardContent className="p-12 text-center text-white">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Protect Your Home?</h2>
                <p className="text-xl text-blue-100 mb-8">Book your free radon measurement test today. No obligation.</p>
                <Link to="/book-test">
                  <Button className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg shadow-lg" data-testid="services-cta-btn">
                    Book Free Test Now <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </main>
  );
}
